import {
  BatteryFull,
  CircleCheck,
  Coffee,
  HeartPulse,
  type LucideIcon,
  Moon,
  Plane,
  Signal,
  Sun,
  Sunset,
  Wifi,
} from "lucide-react";
import {
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TariiWord, withTariiWord } from "../TariiWord";
import { cssZoomOf } from "../siteScale";
import {
  ACTORS,
  type ActorId,
  BLOCKS,
  CARD_LABEL,
  type CardKind,
  type Float,
  GHOSTS,
  MONTH_DAYS,
  MONTH_OFFSET,
  type Mark,
  PUBLISHED_FROM,
  SCENES,
  cardsAt,
} from "./storyScenes";
import { aimAt, chapterStrength, crawlStrength } from "./storyScroll";
import "./how-story.css";

const SPRITES = "/site/characters/";
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CELLS = Array.from({ length: 35 }, (_, i) => i - MONTH_OFFSET + 1);
const RINGS = Array.from({ length: 14 }, (_, i) => `ring-${i}`);
// Each beat plays once: the actors walk in one at a time, each one's cards
// land as they arrive, and everyone stays.
// The pause after the people who stay have moved, before newcomers walk in,
// and before the wider team's cards land.
const TALK_MS = 400;
/** The gap between two newcomers setting off. */
const STAGGER_MS = 260;
/** How long the shown beat takes to play out before the next one comes in:
 *  longer when the picture changes, short when only the people change. */
const LEAVE_MS = 280;
/** How long the page must be still before the chapter rail fades. */
const RAIL_IDLE_MS = 1200;
/** How long the scroll must hold on a part before the stage moves to it. */
const AIM_MS = 60;
const LEAVE_BEAT_MS = 220;
/** How long the next beat stays hidden while it takes its places (a few frames). */
const SET_MS = 32;
// A walk takes longer the further it goes, at an even pace.
function walkMs(from: Mark, to: Mark) {
  const far = Math.hypot(to.x - from.x, (to.y - from.y) * 3);
  return Math.round(Math.max(700, Math.min(1500, 450 + far * 11)));
}

/** Where an actor starts: off the nearer side of the scene. */
function entryOf(mark: Mark): Mark {
  return { ...mark, x: mark.x < 50 ? -64 : 118 };
}
const WALK_FRAMES = ["walk-a", "walk-c", "walk-b", "walk-d"];
const NAMES: Record<ActorId | "team", string> = {
  maya: "Maya",
  jo: "Jo",
  alex: "Alex",
  sam: "Sam",
  team: "Team member",
};
const ICONS: Record<CardKind, LucideIcon> = {
  leave: Plane,
  off: Coffee,
  pref: Sunset,
  day: Sun,
  night: Moon,
  sick: HeartPulse,
  cover: CircleCheck,
};

function prefersReducedMotion() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

function Actor({
  id,
  mark,
  walking,
  walkFor,
  waiting,
  facingLeft,
  talking,
  latest,
  leaving,
}: {
  id: ActorId;
  mark: Mark;
  walking: boolean;
  walkFor: number;
  waiting: boolean;
  facingLeft: boolean;
  talking: boolean;
  latest: boolean;
  leaving: boolean;
}) {
  const meta = ACTORS.find((a) => a.id === id);
  const style = {
    "--hs-ax": `${mark.x}%`,
    "--hs-ay": `${mark.y}%`,
    // The same mark as plain numbers, for the wide-screen walk (a translate).
    "--hs-axn": mark.x,
    "--hs-ayn": mark.y,
    "--hs-walk-ms": `${walkFor}ms`,
  } as CSSProperties;
  const flip = facingLeft && walking ? "hs-flip" : undefined;
  return (
    <div
      className={`hs-actor hs-actor-${id}${walking ? " hs-walking" : ""}${mark.out ? " hs-out" : ""}${waiting ? " hs-waiting" : ""}${mark.seated ? " hs-seated" : ""}${leaving ? " hs-leaving" : ""}${mark.x > 55 ? " hs-say-left" : ""}`}
      style={style}
      aria-hidden="true"
    >
      {mark.say && talking && !mark.out ? (
        <span
          className={`hs-say${mark.sayHigh ? " hs-say-high" : ""}${latest ? "" : " hs-say-quiet"}`}
          key={mark.say}
        >
          {mark.say}
        </span>
      ) : null}
      {walking ? (
        // Four walk frames; CSS shows them in turn.
        <span className={`hs-walk ${flip ?? ""}`}>
          {WALK_FRAMES.map((f) => (
            <img key={f} src={`${SPRITES}${id}/${f}.svg`} alt="" draggable={false} />
          ))}
        </span>
      ) : (
        <img src={`${SPRITES}${id}/${mark.pose ?? "idle"}.svg`} alt="" draggable={false} />
      )}
      <span className="hs-tag">
        <strong>{NAMES[id]}</strong>
        {meta?.role}
      </span>
    </div>
  );
}

export function FloatShot({
  float,
  delay,
  onPeek,
}: {
  float: Float;
  delay: number;
  onPeek: (f: Float | null) => void;
}) {
  const style = { animationDelay: `${delay}ms` } as CSSProperties;
  return (
    <button
      type="button"
      className={`hs-float hs-float-${float.kind}`}
      style={style}
      aria-label={`Enlarge: ${float.alt}`}
      onClick={() => onPeek(float)}
      onMouseEnter={() => onPeek(float)}
      onMouseLeave={() => onPeek(null)}
      onFocus={() => onPeek(float)}
      onBlur={() => onPeek(null)}
    >
      <div className="hs-float-frame">
        <img
          src={float.src}
          alt={float.alt}
          width={float.width}
          height={float.height}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      </div>
      <span className="hs-float-label">
        <span className="hs-float-hover">Hover to view the app</span>
        <span className="hs-float-tap">Tap to view the app</span>
      </span>
    </button>
  );
}

function DeskCalendar({
  step,
  arrived,
  crowd,
  swapped,
  onSwapCell,
  onSwapHover,
}: {
  step: number;
  arrived: ReadonlySet<ActorId>;
  crowd: number;
  swapped: boolean;
  onSwapCell: () => void;
  onSwapHover: (on: boolean) => void;
}) {
  const swap = SCENES[step]?.swap;
  const cards = cardsAt(step).filter(
    (c) =>
      // On the swap's own beat the cover lands only once the visitor swaps.
      !(swap && c.kind === "cover" && c.from === step && !swapped) &&
      (c.from !== step ||
        // The solved shifts land all at once, as the beat opens.
        c.kind === "day" ||
        c.kind === "night" ||
        (c.who === "team" ? (c.ghost ?? 0) < crowd : arrived.has(c.who))),
  );
  return (
    <div className="hs-calendar" aria-hidden="true">
      <div className="hs-cal-back" />
      <div className="hs-cal-paper">
        <i className="hs-cal-side" />
        <i className="hs-cal-base" />
        <div className="hs-rings">
          {RINGS.map((r) => (
            <i key={r} />
          ))}
        </div>
        <div className="hs-cal-head">
          <p>
            October <span>2026</span>
          </p>
          {step >= PUBLISHED_FROM ? (
            <span className="hs-published">
              <CircleCheck size={13} /> Published
            </span>
          ) : null}
          <TariiWord />
        </div>
        <div className="hs-cal-grid">
          {WEEKDAYS.map((d) => (
            <span className="hs-weekday" key={d}>
              {d}
            </span>
          ))}
          {CELLS.map((day) => {
            if (day < 1 || day > MONTH_DAYS) return <span className="hs-blank" key={`b${day}`} />;
            const all = cards.filter((c) => c.day === day);
            const here = all.some((c) => c.kind === "cover")
              ? all.filter((c) => c.kind !== "sick")
              : all;
            const tip = here.map((c) => `${NAMES[c.who]}: ${CARD_LABEL[c.kind]}`).join(" · ");
            return (
              <span
                className={`hs-day${here.length ? " hs-day-busy" : ""}${
                  swap && day === swap.day && arrived.has(swap.in) ? " hs-day-swap" : ""
                }`}
                key={day}
              >
                {swap && day === swap.day && arrived.has(swap.in) ? (
                  // The highlighted day opens the swap. The keyboard path is
                  // the scene's own button beside the pop-up.
                  <button
                    type="button"
                    className="hs-day-hit"
                    tabIndex={-1}
                    aria-label={`Open the cover swap for ${day} October`}
                    onClick={onSwapCell}
                    onPointerEnter={() => onSwapHover(true)}
                    onPointerLeave={() => onSwapHover(false)}
                  />
                ) : null}
                <b>{day}</b>
                {here.map((c) => {
                  const Icon = ICONS[c.kind];
                  return (
                    <span
                      key={`${c.kind}-${c.who}-${c.ghost ?? ""}`}
                      className={`hs-card hs-card-${c.kind}${c.from === step ? " hs-card-new" : ""}`}
                      style={{ "--hs-delay": `${(day % 9) * 70}ms` } as CSSProperties}
                    >
                      <Icon size={10} strokeWidth={2} />
                      <em>{CARD_LABEL[c.kind]}</em>
                      <img src={`${SPRITES}avatars/${c.who}-happy.svg`} alt="" />
                    </span>
                  );
                })}
                {tip && !(swap && day === swap.day && arrived.has(swap.in)) ? (
                  <span className="hs-tip">
                    <strong>{day} October</strong>
                    {tip}
                  </span>
                ) : null}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Two slots side by side over the calendar: the seat on the day and the
 * spare. A click swaps the two people; the swap stays until clicked again.
 */
function SwapPop({
  swap,
  swapped,
  onSwap,
  onClose,
  pinned,
  hover,
  inline = false,
}: {
  swap: { day: number; out: ActorId; in: ActorId };
  swapped: boolean;
  onSwap: () => void;
  onClose: () => void;
  pinned: boolean;
  hover: (on: boolean) => void;
  inline?: boolean;
}) {
  const person = (id: ActorId, inSeat: boolean) => (
    <span
      className={`hs-swap-person hs-swap-${id === swap.out ? "a" : "b"} hs-swap-${
        inSeat ? "seat" : "spare"
      }${id === swap.out ? " hs-swap-out" : ""}`}
    >
      <img src={`${SPRITES}avatars/${id}-happy.svg`} alt="" />
      <strong>{NAMES[id]}</strong>
      <em>{id === swap.out ? "Off sick" : inSeat ? "On shift" : "Free"}</em>
    </span>
  );
  return (
    <div
      className={`hs-swap-pop${inline ? " hs-swap-inline" : ""}`}
      onPointerEnter={() => hover(true)}
      onPointerLeave={() => hover(false)}
    >
      {pinned ? (
        <button type="button" className="hs-swap-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
      ) : null}
      <p className="hs-swap-head">
        {swap.day} October · {swapped ? "covered" : "1 seat open"}
      </p>
      <div className={`hs-swap-slots${swapped ? " hs-swapped" : ""}`}>
        <span className="hs-swap-slot">The seat</span>
        <span className="hs-swap-slot">Spare</span>
        {person(swap.out, !swapped)}
        {person(swap.in, swapped)}
      </div>
      <button type="button" className="hs-swap-btn" aria-pressed={swapped} onClick={onSwap}>
        {swapped ? "Swap back" : "Swap"}
      </button>
    </div>
  );
}

function PlannerScreen({ step }: { step: number }) {
  const scene = SCENES[step];
  return (
    <div className="hs-monitor" aria-hidden={scene?.screen ? undefined : true}>
      <div className="hs-bezel">
        <div className="hs-app-bar">
          <i />
          <i />
          <i />
          <TariiWord />
        </div>
        <div className="hs-app">
          {SCENES.map((s, i) =>
            // Only this beat's screen and its neighbours stay in the page,
            // so a dozen large captures are never decoded and painted at once.
            s.screen && Math.abs(i - step) <= 1 ? (
              <img
                key={s.screen.src}
                src={s.screen.src}
                alt={i === step ? s.screen.alt : ""}
                aria-hidden={i === step ? undefined : true}
                className={i === step ? "hs-app-on" : undefined}
                loading="lazy"
                decoding="async"
              />
            ) : null,
          )}
        </div>
      </div>
      <div className="hs-neck" />
      <div className="hs-foot" />
    </div>
  );
}

/** One part of the scrolling words: a chapter title, one sentence, or the "In Tarii" line. */
interface Part {
  key: string;
  /** The block of words this part belongs to; blocks count across chapters. */
  block: number;
  /** The beat (index in SCENES) the stage plays while this part is in the middle. */
  scene: number;
  kind: "title" | "line" | "tarii";
  text: string;
}

const PARTS: ReadonlyArray<Part> = BLOCKS.flatMap((b, block) => {
  const first = b.beats[0]?.scene ?? 0;
  const last = b.beats[b.beats.length - 1]?.scene ?? first;
  return [
    { key: `${b.id}-title`, block, scene: first, kind: "title" as const, text: b.title },
    ...b.beats.map((beat) => ({
      key: `${b.id}-${beat.scene}`,
      block,
      scene: beat.scene,
      kind: "line" as const,
      text: beat.text,
    })),
    { key: `${b.id}-in`, block, scene: last, kind: "tarii" as const, text: b.inTarii },
  ];
});

/** How far a chapter block glides up per sentence of scroll, in pixels (#2996). */
const GLIDE_PX = 10;

/**
 * The sentences (#2810): a chapter block shows its title, all its sentences
 * and its "In Tarii" line together, and moves as one (#2996). The sentence
 * the reader is on reads strongest. `LINES` holds the part index of each sentence, `LINE_BLOCK` its block.
 */
const LINES = PARTS.flatMap((p, i) => (p.kind === "line" ? [i] : []));
const LINE_BLOCK = LINES.map((i) => PARTS[i]?.block ?? 0);
/** Each part's place in `LINES`, or -1 for a title or an "In Tarii" line. */
const LINE_OF = PARTS.map((_, i) => LINES.indexOf(i));
const IS_LINE = new Set(LINES);

/** The first part of a block. */
function firstPart(block: number) {
  return Math.max(
    0,
    PARTS.findIndex((p) => p.block === block),
  );
}

/**
 * The /how-it-works story: one pinned stage with the words in place at its
 * left. The page scrolls past a column of unseen markers, one per part
 * (each chapter's title, its sentences, then its "In Tarii" line). The
 * marker in the middle of the screen says how far the story has come: the
 * words show the chapter's parts up to that one, each coming up under the
 * last, and the stage plays its beat. The scene follows the reader's pace. The
 * characters walk to their marks, the desk calendar turns into the planner's
 * screen and back, and the beat's real screens hover beside it (hover or
 * focus one to enlarge).
 */
export function HowItWorksStory({
  startChapter,
  onChapter,
  title,
}: {
  /** The block the page opened on (from the hash). */
  startChapter: number;
  /** Called when the reader scrolls into another chapter. */
  onChapter: (index: number) => void;
  /** The page title, shown small in the stage's top left corner (#2809). */
  title?: ReactNode;
}) {
  const [calm] = useState(prefersReducedMotion);
  const flowRef = useRef<HTMLDivElement>(null);
  // The part whose marker is in the middle of the screen.
  const [activePart, setActivePart] = useState(() => firstPart(startChapter));
  const part = PARTS[activePart] ?? PARTS[0];
  // The beat the scroll asks for, and the beat the stage shows. A change plays
  // the shown beat out first (data-leaving), then the stage moves on and the
  // next beat comes in, so the two never run at once (#2809).
  // The scroll can cross a part and come back (a snap that settles on the
  // nearer chapter, a hand that stops on a boundary). The stage takes a part
  // only once it has held for AIM_MS, so a flick there and back never starts
  // a leave that is then called off (#2809).
  const asked = part?.scene ?? 0;
  const [wanted, setWanted] = useState(asked);
  useEffect(() => {
    if (asked === wanted) return;
    const t = window.setTimeout(() => setWanted(asked), calm ? 0 : AIM_MS);
    return () => window.clearTimeout(t);
  }, [asked, wanted, calm]);
  const [step, setStep] = useState(wanted);
  // After a change of picture the next beat is set up unseen (data-leaving
  // stays "picture" for SET_MS): the boxes and the people take their new
  // places while hidden, and only then does the beat fade in.
  const [setting, setSetting] = useState(false);
  useEffect(() => {
    if (wanted === step) return;
    const from = SCENES[step];
    const to = SCENES[wanted];
    const picture = from?.mode !== to?.mode || Boolean(from?.split) !== Boolean(to?.split);
    const t = window.setTimeout(
      () => {
        setStep(wanted);
        setSetting(picture && !calm);
      },
      calm ? 0 : picture ? LEAVE_MS : LEAVE_BEAT_MS,
    );
    return () => window.clearTimeout(t);
  }, [wanted, step, calm]);
  useEffect(() => {
    if (!setting) return;
    const t = window.setTimeout(() => setSetting(false), SET_MS);
    return () => window.clearTimeout(t);
  }, [setting]);
  const block = part?.block ?? 0;
  const chapter = block;
  // The block in view, for the words' draw loop (see aimAt), which redraws
  // when it changes.
  const blockNow = useRef(block);
  blockNow.current = block;
  const redrawWords = useRef<() => void>(() => {});
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new block is the trigger.
  useEffect(() => {
    redrawWords.current();
  }, [block]);
  // The hash follows the chapter in the middle of the screen.
  const reported = useRef(startChapter);
  useEffect(() => {
    if (reported.current === chapter) return;
    reported.current = chapter;
    onChapter(chapter);
  }, [chapter, onChapter]);
  const scene = SCENES[step] ?? SCENES[0];
  const next = SCENES[wanted] ?? scene;
  // "picture" when the calendar, the screen or the split changes: that piece
  // plays out too. "beat" when only the people and the words change.
  const leaving =
    wanted === step
      ? setting
        ? "picture"
        : undefined
      : next?.mode !== scene?.mode || Boolean(next?.split) !== Boolean(scene?.split)
        ? "picture"
        : "beat";
  const stageRef = useRef<HTMLDivElement>(null);
  // The sick-day cover swap: the visitor makes it, and it stays made.
  const [swapped, setSwapped] = useState(false);
  // The pop-up opens only from the highlighted day, and closes on a new beat.
  const [swapPin, setSwapPin] = useState(false);
  // Narrow screens show the swap card open, under the chapter's words.
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(max-width: 1023px)");
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  const [swapPeek, setSwapPeek] = useState(false);
  // Phones show one screen of a split beat large and the other small in the
  // corner; a tap on the small one swaps them, with a short fade (#2809).
  const [bigMember, setBigMember] = useState(false);
  // The chapter rail shows while the page scrolls and fades once it has been
  // still for RAIL_IDLE_MS (#2809). Only the change of state renders, not
  // every scroll event.
  const [railAwake, setRailAwake] = useState(false);
  useEffect(() => {
    let timer = 0;
    let awake = false;
    const wake = () => {
      if (!awake) {
        awake = true;
        setRailAwake(true);
      }
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        awake = false;
        setRailAwake(false);
      }, RAIL_IDLE_MS);
    };
    window.addEventListener("scroll", wake, { passive: true });
    return () => {
      window.removeEventListener("scroll", wake);
      window.clearTimeout(timer);
    };
  }, []);
  const [viewFade, setViewFade] = useState(false);
  const splitBeat = Boolean(scene?.split);
  useEffect(() => {
    if (!splitBeat) setBigMember(false);
  }, [splitBeat]);
  useEffect(() => {
    if (!viewFade) return;
    const t = window.setTimeout(
      () => {
        setBigMember((v) => !v);
        setViewFade(false);
      },
      calm ? 0 : 160,
    );
    return () => window.clearTimeout(t);
  }, [viewFade, calm]);
  const swapLeave = useRef(0);
  useEffect(() => {
    if (step >= 0) {
      setSwapPin(false);
      setSwapPeek(false);
    }
  }, [step]);
  // Hovering the day floats the card up; a short grace lets the pointer
  // travel from the day onto the card without it closing.
  function swapHover(on: boolean) {
    window.clearTimeout(swapLeave.current);
    if (on) setSwapPeek(true);
    else swapLeave.current = window.setTimeout(() => setSwapPeek(false), 250);
  }

  const [peek, setPeek] = useState<Float | null>(null);
  // Who has arrived and who is walking, for the step they belong to: a
  // change of step empties both at once, so nobody slides to a new mark.
  const [cast, setCast] = useState<{
    step: number;
    arrived: ReadonlySet<ActorId>;
    walking: ReadonlySet<ActorId>;
  }>({ step: -1, arrived: new Set(), walking: new Set() });
  // The wider team's cards on a crowd scene: -1 not yet, else how many have
  // landed. They land together once the cast has spoken.
  const [crowd, setCrowd] = useState(-1);
  // A beat plays once. Scrolled back to, its end state shows at once.
  const played = useRef(new Set<number>());
  // The beat we came from: an actor who was already on stage walks on from
  // where they stood, instead of leaving and coming back in.
  const [path, setPath] = useState({ step, from: -1, seen: false });
  if (path.step !== step) setPath({ step, from: path.step, seen: played.current.has(step) });
  const still = path.step === step && path.seen;
  const before = path.step === step ? SCENES[path.from] : undefined;
  // Where each actor stood on screen at the last render, if they were seen.
  // A beat can change before its cast has all walked in: the next beat walks
  // on from here, not from the old beat's marks, so nobody jumps (#2809).
  const placed = useRef<Partial<Record<ActorId, Mark>>>({});
  const order = useMemo(
    () => scene?.order ?? ACTORS.map((a) => a.id).filter((id) => !scene?.actors[id].out),
    [scene],
  );
  const starts = useMemo(() => {
    const out = {} as Record<ActorId, { mark: Mark; stayed: boolean; moves: boolean; ms: number }>;
    for (const { id } of ACTORS) {
      const mark = scene?.actors[id];
      const prev = before ? placed.current[id] : undefined;
      const stayed = Boolean(prev);
      // A new picture is set up unseen: whoever stays is already at their new
      // mark when it fades in, so nobody slides across it.
      const fresh =
        before?.mode !== scene?.mode || Boolean(before?.split) !== Boolean(scene?.split);
      const start = prev ? (fresh && mark ? mark : prev) : mark ? entryOf(mark) : undefined;
      if (!mark || !start) continue;
      const moves = Math.abs(start.x - mark.x) > 3 || start.y !== mark.y;
      out[id] = { mark: start, stayed, moves, ms: moves ? walkMs(start, mark) : 250 };
    }
    return out;
  }, [scene, before]);

  useEffect(() => {
    setPeek(null);
    played.current.add(step);
    const none = new Set<ActorId>();
    if (still || calm) {
      setCast({ step, arrived: new Set(order), walking: none });
      setCrowd(scene?.crowd ? GHOSTS.length : -1);
      return;
    }
    setCast({ step, arrived: none, walking: none });
    setCrowd(-1);
    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => timers.push(window.setTimeout(fn, ms));
    const arrive = (ids: ActorId[]) =>
      setCast((c) => ({
        step,
        arrived: new Set([...c.arrived, ...ids]),
        walking: new Set([...c.walking].filter((id) => !ids.includes(id))),
      }));
    const walk = (ids: ActorId[]) =>
      setCast((c) => ({ ...c, step, walking: new Set([...c.walking, ...ids]) }));
    // Everyone still on stage from the last beat moves at once, and the
    // newcomers walk in with them, each a moment after the one before, so the
    // beat is in place within about a walk (#2809). They speak as they arrive.
    const stayers = order.filter((id) => starts[id]?.stayed);
    const comers = order.filter((id) => !starts[id]?.stayed);
    const t0 = 80;
    let end = t0;
    if (stayers.length) {
      at(t0, () => walk(stayers.filter((id) => starts[id]?.moves)));
      const ms = Math.max(...stayers.map((id) => starts[id]?.ms ?? 250));
      at(t0 + ms, () => arrive(stayers));
      end = t0 + ms;
    }
    comers.forEach((id, n) => {
      const go = t0 + (stayers.length ? TALK_MS : 0) + n * STAGGER_MS;
      const ms = starts[id]?.ms ?? 1100;
      at(go, () => walk([id]));
      at(go + ms, () => arrive([id]));
      end = Math.max(end, go + ms);
    });
    if (scene?.crowd) at(end + TALK_MS, () => setCrowd(GHOSTS.length));
    return () => {
      for (const id of timers) window.clearTimeout(id);
    };
  }, [order, starts, scene, step, still, calm]);

  // The marker that crosses the middle of the screen sets the part.
  useEffect(() => {
    const flow = flowRef.current;
    if (!flow || typeof IntersectionObserver !== "function") return;
    const middle = new IntersectionObserver(
      (entries) => {
        const hit = entries.filter((e) => e.isIntersecting).pop();
        if (hit) setActivePart(Number((hit.target as HTMLElement).dataset.part));
      },
      { rootMargin: "-46% 0px -46% 0px" },
    );
    for (const n of flow.querySelectorAll("[data-part]")) middle.observe(n);
    return () => middle.disconnect();
  }, []);
  // Scrolling (#2809, in place of the #2810 wheel step): the browser scrolls
  // natively, with its own momentum, and the page never moves itself. The
  // words show whole sentences on their own, so a rest needs no snap.
  // The crawl: one block of words per chapter, stacked in a window at the
  // left of the stage. A block holds the chapter title, all its sentences and
  // its "In Tarii" line, and it moves as one: it glides up a little with the
  // scroll, and between two chapters it fades out in place while the next one
  // fades in. The sentence the reader is on reads strongest. The scroll
  // position between two sentence markers sets how far the block has moved.
  const crawlRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const flow = flowRef.current;
    const crawl = crawlRef.current;
    const track = trackRef.current;
    if (!flow || !crawl || !track) return;
    // Measured once (and again on a resize), so a scroll frame reads no layout.
    let marks: number[] = [];
    let blocks: HTMLElement[] = [];
    let caps: HTMLElement[] = [];
    let anchor = 0;
    const firstLine = BLOCKS.map((_, c) => LINE_BLOCK.indexOf(c));
    const lastLine = BLOCKS.map((_, c) => LINE_BLOCK.lastIndexOf(c));
    const measure = () => {
      const top = window.scrollY;
      marks = Array.from(flow.querySelectorAll<HTMLElement>("[data-part]"))
        .filter((m) => IS_LINE.has(Number(m.dataset.part)))
        .map((m) => {
          const r = m.getBoundingClientRect();
          return r.top + top + r.height / 2;
        });
      blocks = Array.from(track.children) as HTMLElement[];
      caps = Array.from(track.querySelectorAll<HTMLElement>(".hs-cap"));
      // How far down its window the block starts, from the CSS.
      const at = Number.parseFloat(getComputedStyle(crawl).getPropertyValue("--hs-crawl-anchor"));
      anchor = crawl.clientHeight * (Number.isFinite(at) ? at : 0.16);
    };
    /** The fractional sentence at the middle of the screen. */
    const lineAt = () => {
      const mid = window.scrollY + window.innerHeight / 2;
      const last = marks.length - 1;
      if (last < 0 || mid <= (marks[0] ?? 0)) return 0;
      if (mid >= (marks[last] ?? 0)) return last;
      for (let i = 0; i < last; i++) {
        const a = marks[i] ?? 0;
        const b = marks[i + 1] ?? 0;
        if (mid < b) return i + (mid - a) / (b - a || 1);
      }
      return last;
    };
    let shown = -1;
    // The sentence the words show: the one the reader is scrolling towards.
    let aim = -1;
    let before = -1;
    let frame = 0;
    const draw = () => {
      frame = 0;
      const raw = lineAt();
      // The words go straight to the next sentence as soon as the scroll
      // moves towards it, and do not wait for the page to come to rest or
      // for the stage (#2809). Only a move changes the sentence, in its own
      // direction: at rest the words stay put, so a rest half way between two
      // sentences never flips between them.
      const moved = before < 0 ? 0 : raw - before;
      before = raw;
      aim = calm ? Math.round(raw) : aimAt(raw, aim, moved, blockNow.current, LINE_BLOCK);
      const target = calm ? raw : aim;
      // A short ease (about 150 ms) so the move reads as a move, not a jump.
      shown = shown < 0 || calm ? target : shown + (target - shown) * 0.3;
      if (Math.abs(target - shown) < 0.002) shown = target;
      // The chapter in view: whole between two sentences of one chapter,
      // part way between two chapters.
      const k = Math.max(0, Math.min(LINES.length - 1, Math.floor(shown)));
      const from = LINE_BLOCK[k] ?? 0;
      const to = LINE_BLOCK[Math.min(LINES.length - 1, k + 1)] ?? from;
      const blockAt = from + (to - from) * (shown - k);
      // The blocks share one place: between two chapters the old block lifts
      // a little and fades while the new one rises into place, so no words
      // travel down over the people. Inside a chapter the whole block glides
      // up a little with the scroll; no part of it stays behind.
      track.style.transform = `translate3d(0, ${anchor.toFixed(1)}px, 0)`;
      for (const [c, b] of blocks.entries()) {
        const first = firstLine[c] ?? 0;
        const span = Math.max(0, (lastLine[c] ?? first) - first);
        const glide = Math.max(0, Math.min(span, shown - first)) * GLIDE_PX;
        b.style.transform = `translate3d(0, ${((c - blockAt) * 40 - glide).toFixed(1)}px, 0)`;
        b.style.visibility = Math.abs(c - blockAt) < 1 ? "visible" : "hidden";
      }
      // Only the chapter in view shows. In it the title and the "In Tarii"
      // line read in full, and a sentence reads strongest when it is the one
      // the reader is on.
      for (const [i, c] of caps.entries()) {
        const owner = PARTS[i]?.block ?? 0;
        let o = chapterStrength(owner - blockAt);
        const line = LINE_OF[i] ?? -1;
        if (line >= 0) o *= 0.55 + 0.45 * crawlStrength(Math.abs(line - shown) / 2);
        c.style.opacity = o.toFixed(3);
      }
      if (shown !== target) frame = window.requestAnimationFrame(draw);
    };
    const ask = () => {
      if (!frame) frame = window.requestAnimationFrame(draw);
    };
    redrawWords.current = ask;
    const remeasure = () => {
      measure();
      ask();
    };
    measure();
    draw();
    window.addEventListener("scroll", ask, { passive: true });
    window.addEventListener("resize", remeasure);
    // The window can change size without a scroll (the phone swap card
    // shortens it), so a size change measures again.
    const sized = typeof ResizeObserver === "function" ? new ResizeObserver(remeasure) : null;
    sized?.observe(crawl);
    sized?.observe(flow);
    return () => {
      window.removeEventListener("scroll", ask);
      window.removeEventListener("resize", remeasure);
      sized?.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [calm]);
  // The chapter rail: a click scrolls that chapter's name to the middle.
  // The page then settles on the chapter's first pair.
  function goToChapter(index: number) {
    const id = BLOCKS[index]?.id;
    document
      .getElementById(`${id}-title`)
      ?.scrollIntoView({ behavior: calm ? "auto" : "smooth", block: "center" });
  }

  const current = cast.step === step;
  const arrived: ReadonlySet<ActorId> = current ? cast.arrived : new Set();
  const walkingNow: ReadonlySet<ActorId> = current ? cast.walking : new Set();
  const beat = arrived.size;

  // Pointer tracking: each move sets a target; one frame loop eases the
  // readings towards it, so the turn stays steady however the events arrive.
  const aim = useRef({ tx: 0, ty: 0, px: 0, py: 0 });
  const now = useRef({ tx: 0, ty: 0, px: 0, py: 0 });
  const frame = useRef(0);
  useEffect(() => () => window.cancelAnimationFrame(frame.current), []);
  function ease() {
    // Each reading goes on the one element that reads it: the device for the
    // calendar and monitor, the handset for the phone.
    const device = stageRef.current?.querySelector<HTMLElement>(".hs-device");
    const handset = stageRef.current?.querySelector<HTMLElement>(".hs-handset");
    const a = aim.current;
    const n = now.current;
    let busy = false;
    for (const k of ["tx", "ty", "px", "py"] as const) {
      const d = a[k] - n[k];
      n[k] = Math.abs(d) < 0.001 ? a[k] : n[k] + d * 0.14;
      if (n[k] !== a[k]) busy = true;
      (k === "tx" || k === "ty" ? device : handset)?.style.setProperty(
        `--hs-${k}`,
        n[k].toFixed(4),
      );
    }
    frame.current = busy ? window.requestAnimationFrame(ease) : 0;
  }
  function kick() {
    if (!frame.current) frame.current = window.requestAnimationFrame(ease);
  }

  function tilt(e: PointerEvent<HTMLDivElement>) {
    const el = stageRef.current;
    // Measured against each element's layout box inside the scene, which
    // ignores its own turn, so the reading stays steady while it turns.
    const box = el?.querySelector<HTMLElement>(".hs-scene");
    if (!el || !box || e.pointerType !== "mouse") return;
    const r = box.getBoundingClientRect();
    const clamp = (v: number) => Math.max(-0.5, Math.min(0.5, v));
    const read = (selector: string): [number, number] => {
      const node = box.querySelector<HTMLElement>(selector);
      if (!node) return [0, 0];
      // Offsets are in the root's own px; the rect and the pointer in screen
      // px, which differ under the laptop zoom (#2817).
      const z = cssZoomOf(node);
      const w = (node.offsetWidth || 1) * z;
      const h = (node.offsetHeight || 1) * z;
      const cx = r.left + node.offsetLeft * z + w / 2;
      const cy = r.top + node.offsetTop * z + h / 2;
      return [clamp((e.clientX - cx) / w), clamp((e.clientY - cy) / h)];
    };
    const [tx, ty] = read(".hs-device");
    const [px, py] = read(".hs-handset");
    aim.current = { tx, ty, px, py };
    el.querySelector<HTMLElement>(".hs-device")?.style.setProperty("--hs-track", "1");
    kick();
  }
  function untilt() {
    aim.current = { tx: 0, ty: 0, px: 0, py: 0 };
    stageRef.current
      ?.querySelector<HTMLElement>(".hs-device")
      ?.style.setProperty("--hs-track", "0");
    kick();
  }

  // Each actor's props for this render, and where the seen ones stand.
  const players = ACTORS.flatMap(({ id }) => {
    const mark = scene?.actors[id];
    if (!mark) return [];
    const here = mark.out || arrived.has(id) || walkingNow.has(id);
    const from = starts[id];
    const start = from?.mark ?? entryOf(mark);
    const waiting = !here && !from?.stayed;
    return [
      {
        id,
        mark: here ? mark : start,
        waiting,
        walking: walkingNow.has(id),
        facingLeft: mark.x < start.x,
        talking: arrived.has(id),
        // Only the newest speaker's bubble shows, so neighbours never talk
        // over each other; hover brings any back.
        latest: [...arrived].pop() === id,
        walkFor: from?.ms ?? 1100,
        // Not in the next beat: they fade while the shown beat plays out.
        leaving: Boolean(leaving) && next?.actors[id].out === true,
        seen: !waiting && !(here ? mark : start).out,
      },
    ];
  });
  useEffect(() => {
    placed.current = Object.fromEntries(players.filter((a) => a.seen).map((a) => [a.id, a.mark]));
  });

  return (
    <div className="hs">
      <div className="hs-sticky">
        <div
          ref={stageRef}
          className="hs-stage"
          data-mode={scene?.mode}
          data-split={scene?.split ? "" : undefined}
          data-leaving={leaving}
          data-big={splitBeat && bigMember ? "member" : undefined}
          data-rail-awake={railAwake ? "" : undefined}
          data-view-fade={viewFade ? "" : undefined}
          data-step={step + 1}
          onPointerMove={tilt}
          onPointerLeave={untilt}
        >
          {title ? <h1 className="hs-title">{title}</h1> : null}
          <div className="hs-floor" aria-hidden="true" />
          {/* The crawl; screen readers read the markers below instead. */}
          <div className="hs-crawl" aria-hidden="true" ref={crawlRef}>
            <div className="hs-crawl-track" ref={trackRef}>
              {BLOCKS.map((c, ci) => {
                const own = PARTS.map((p, i) => [p, i] as const).filter(([p]) => p.block === ci);
                const cap = ([p, i]: readonly [Part, number]) => (
                  <p
                    key={p.key}
                    className={`hs-cap hs-cap-${p.kind}${
                      i === activePart
                        ? " hs-cap-on"
                        : i === activePart + 1
                          ? " hs-cap-next"
                          : i < activePart
                            ? " hs-cap-past"
                            : " hs-cap-later"
                    }`}
                  >
                    {p.kind === "tarii" ? (
                      <>
                        <strong>
                          In <TariiWord />:
                        </strong>{" "}
                        {p.text}
                      </>
                    ) : (
                      withTariiWord(p.text)
                    )}
                  </p>
                );
                return (
                  <div className="hs-crawl-ch" key={c.id}>
                    {own.map(cap)}
                  </div>
                );
              })}
            </div>
          </div>
          <nav className="hs-rail" aria-label="Chapters">
            <ol>
              {BLOCKS.map((c, i) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={i < chapter ? "hs-rail-done" : undefined}
                    aria-current={i === chapter ? "step" : undefined}
                    onClick={() => goToChapter(i)}
                  >
                    <span className="hs-rail-name">{withTariiWord(c.title)}</span>
                  </button>
                </li>
              ))}
            </ol>
          </nav>
          <div className={`hs-scene${still ? " hs-still" : ""}`}>
            <img className="hs-plant" src={`${SPRITES}props/plant.svg`} alt="" aria-hidden="true" />
            <div className="hs-device">
              <DeskCalendar
                step={step}
                arrived={arrived}
                crowd={crowd}
                swapped={swapped}
                onSwapCell={() => setSwapPin((v) => !v)}
                onSwapHover={swapHover}
              />
              <PlannerScreen step={step} />
            </div>
            {scene?.split ? (
              <div className="hs-handset" key={scene.split.src}>
                <div className="hs-handset-frame">
                  <i className="hs-handset-key hs-handset-key-a" />
                  <i className="hs-handset-key hs-handset-key-b" />
                  <i className="hs-handset-key hs-handset-key-c" />
                  <div className="hs-handset-screen">
                    <div className="hs-handset-status" aria-hidden="true">
                      <span>9:41</span>
                      <i className="hs-handset-island" />
                      <span className="hs-handset-icons">
                        <Signal size={12} strokeWidth={2.4} />
                        <Wifi size={12} strokeWidth={2.4} />
                        <BatteryFull size={15} strokeWidth={2} />
                      </span>
                    </div>
                    <img
                      src={scene.split.src}
                      alt={scene.split.alt}
                      width={scene.split.width}
                      height={scene.split.height}
                      decoding="async"
                      draggable={false}
                    />
                    <i className="hs-handset-home" />
                  </div>
                </div>
              </div>
            ) : null}
            {scene?.split ? (
              <button
                type="button"
                className="hs-view-swap"
                aria-label={
                  bigMember ? "Show the planner's screen large" : "Show the member's phone large"
                }
                aria-pressed={bigMember}
                onClick={() => setViewFade(true)}
              />
            ) : null}
            {scene?.swap && arrived.has(scene.swap.in) ? (
              swapPin || swapPeek || narrow ? (
                <SwapPop
                  swap={scene.swap}
                  swapped={swapped}
                  pinned={swapPin && !narrow}
                  inline={narrow}
                  hover={swapHover}
                  onSwap={() => {
                    setSwapped((v) => !v);
                    setSwapPin(true);
                  }}
                  onClose={() => {
                    setSwapPin(false);
                    setSwapPeek(false);
                  }}
                />
              ) : (
                <button type="button" className="hs-sr-only" onClick={() => setSwapPin(true)}>
                  Open the cover swap for {scene.swap.day} October
                </button>
              )
            ) : null}
            {players.map(({ seen: _seen, ...a }) => (
              <Actor key={a.id} {...a} />
            ))}
          </div>
          <div className="hs-shelf" key={`shelf-${step}`}>
            {scene?.floats.map((f, n) =>
              beat >= Math.min(n + 1, order.length) ? (
                <FloatShot key={f.src} float={f} delay={0} onPeek={setPeek} />
              ) : null,
            )}
          </div>
          {peek ? (
            <div className={`hs-peek hs-peek-${peek.kind}`} aria-hidden="true" key={peek.src}>
              <img src={peek.src} alt="" width={peek.width} height={peek.height} />
            </div>
          ) : null}
        </div>
      </div>
      <div className="hs-flow" ref={flowRef}>
        {BLOCKS.map((c, ci) => (
          <section key={c.id} id={c.id} className="hs-chapter" aria-labelledby={`${c.id}-title`}>
            {PARTS.map((p, i) => {
              if (p.block !== ci) return null;
              const cls = `hs-part${i === activePart ? " hs-part-on" : ""}`;
              if (p.kind === "title") {
                return (
                  <h2 key={p.key} id={`${c.id}-title`} className={cls} data-part={i}>
                    {withTariiWord(p.text)}
                  </h2>
                );
              }
              if (p.kind === "tarii") {
                return (
                  <p key={p.key} className={cls} data-part={i}>
                    <strong>
                      In <TariiWord />:
                    </strong>{" "}
                    {p.text}
                  </p>
                );
              }
              return (
                <p key={p.key} className={cls} data-part={i}>
                  {withTariiWord(p.text)}
                </p>
              );
            })}
          </section>
        ))}
      </div>
    </div>
  );
}
